/**
 * Color utility functions for dynamic contrast adjustments
 */

/**
 * Extracts the lightness value from an OKLCH color string
 * @param oklchColor - Color string in format "oklch(L C H)"
 * @returns Lightness value (0-1) or null if parsing fails
 */
function parseOklchLightness(oklchColor: string): number | null {
  const match = oklchColor.match(/oklch\(([0-9.]+)\s+[0-9.]+\s+[0-9.]+\)/);
  if (match && match[1]) {
    return parseFloat(match[1]);
  }
  return null;
}

/**
 * Determines whether to use light or dark text based on background color
 * Uses WCAG AAA contrast guidelines (7:1 ratio for enhanced readability)
 *
 * @param backgroundColor - Background color in OKLCH format
 * @returns Color string for text (high-contrast white or dark text)
 */
export function getContrastColor(backgroundColor: string): string {
  const lightness = parseOklchLightness(backgroundColor);

  if (lightness === null) {
    // Fallback to dark text if parsing fails
    return "oklch(0.20 0.02 140)";
  }

  // Enhanced threshold at 0.65 for better readability
  // Use bright white text on medium-dark and dark backgrounds
  if (lightness < 0.65) {
    return "oklch(1.0 0 0)"; // Pure white in OKLCH
  }

  // Light backgrounds use darker text for maximum contrast
  return "oklch(0.20 0.02 140)";
}

/**
 * Gets the appropriate muted text color based on background
 * Used for secondary text like zone numbers and descriptions
 *
 * @param backgroundColor - Background color in OKLCH format
 * @returns Color string for muted text with enhanced contrast
 */
export function getMutedContrastColor(backgroundColor: string): string {
  const lightness = parseOklchLightness(backgroundColor);

  if (lightness === null) {
    return "oklch(0.45 0.02 140)";
  }

  if (lightness < 0.65) {
    // Brighter muted text on dark backgrounds (95% opacity white for better readability)
    return "rgba(255, 255, 255, 0.95)";
  }

  // Darker muted text on light backgrounds for better contrast
  return "oklch(0.45 0.02 140)";
}
